import { FC, useEffect, useId } from "react";

import { Box, Stack } from "@mui/material";
import {
  convertCurrencyToCurrencySymbol,
  FormType,
  getCurrencyFromCountry,
  getZeroAmountPlaceholder,
  isCurrencySymbolAfterAmount,
} from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { DonationPaymentInput, useFormV2Context } from "../../../contexts/FormV2Context";
import { GiftAidBlock } from "../donation-selection/GiftAidBlock/GiftAidBlock";

import { useAdditionalDonationAmountField } from "./helpers/useAdditionalDonationAmountField";

import { useShapes } from "@/canvas/components/BackgroundShapes/ShapesContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { blockNonAmountKeys } from "@/common/helpers/blockNonAmountKeys";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { FORM_ELEMENT_IDS } from "@/features/LiveFormEditor/Steps/helpers/formStepToElementIdMap";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { CampaignsIdle } from "@/icons/special";

export const TicketingV2AdditionalDonation: FC = () => {
  const { formObject: ticketing, form } = useFormV2Context(FormType.Ticketing);
  const { locale } = useLocaleContext();
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const titleId = useId();
  const {
    extraDonation,
    extraDonationAmount,
    extraDonationAmountField,
    manualExtraDonationField,
    manualExtraDonationFieldRef,
    onManualExtraDonationFieldBlur,
    handleOnManualExtraDonationChange,
    logExtraDonationChangedEvent,
    errorMessage,
  } = useAdditionalDonationAmountField();
  const { isPreview } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingV2AdditionalDonation");
  const { backgroundColor } = useShapes();
  const organizationIsGiftAidEligible = ticketing.organization?.isGiftAidEligible;
  const isExtraDonationReceiptEligible = Boolean(
    ticketing.organization?.canGenerateReceipt && ticketing.generateExtraDonationReceipt
  );
  const hasExtraDonationOnPreview = Boolean(!isGhostPreview);
  const donorCanOptInGiftAidOnForm = Boolean(organizationIsGiftAidEligible && extraDonation);
  const donorCanOptInGiftAidOnPreview = Boolean(
    organizationIsGiftAidEligible && hasExtraDonationOnPreview && isPreview
  );
  const donorCanOptInGiftAid = isPreview ? donorCanOptInGiftAidOnPreview : donorCanOptInGiftAidOnForm;

  useEffect(() => {
    if (!isPreview || !organizationIsGiftAidEligible) {
      return;
    }

    if (donorCanOptInGiftAidOnPreview) {
      form.setValue("extraDonation", 100_00);
      form.setValue("manualExtraDonation", "100");
    } else {
      form.setValue("extraDonation", undefined);
      form.setValue("manualExtraDonation", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorCanOptInGiftAidOnPreview]);

  if (!ticketing.extraDonation) {
    return null;
  }

  const isDisabled = ticketing.isArchived;

  return (
    <>
      <Stack
        direction="column"
        gap={1.5}
        padding={{ xs: 1.5, lg: 2 }}
        borderRadius={1}
        sx={{
          border: (theme) => `1px solid ${theme.palette.formsComponents.divider.borderColor}`,
          pointerEvents: isDisabled ? "none" : "auto",
          opacity: isDisabled && !isGhostPreview ? 0.4 : 1,
          backgroundColor: (theme) =>
            extraDonation > 0 ? theme.palette.forms.surface.supershy : isSmallScreen ? "transparent" : backgroundColor,
          ...(isGhostPreview && {
            opacity: GHOST_PREVIEW_OPACITY,
          }),
        }}
        data-test="themed-item-block-card"
      >
        <Stack direction="column">
          <Stack direction="row" gap={1} alignItems="center">
            <FormIcon size="medium" sx={(theme) => ({ color: theme.palette.forms.text.brand })}>
              <CampaignsIdle />
            </FormIcon>
            <FormTypography id={titleId} variant="h6" vibe="intense">
              {t("ticketing", "addExtraDonationTitle")}
            </FormTypography>
          </Stack>
          <FormTypography variant="body2" vibe="intense">
            {t("ticketing", "addExtraDonationSubtitle", { organizationName: ticketing.organization?.name })}
          </FormTypography>
        </Stack>
        <Stack direction="column" gap={1.5} alignItems="flex-start">
          <Box sx={{ width: "144px" }}>
            <input type="hidden" value={extraDonationAmount ?? ""} {...extraDonationAmountField} />
            <FormTextField
              id={FORM_ELEMENT_IDS["add-donation"]}
              inputRef={manualExtraDonationFieldRef}
              type="text"
              inputProps={{ inputMode: "decimal", onKeyPress: blockNonAmountKeys, "aria-labelledby": titleId }}
              startAdornment={
                isCurrencySymbolAfterAmount(locale)
                  ? undefined
                  : convertCurrencyToCurrencySymbol(getCurrencyFromCountry(ticketing.organization.country))
              }
              endAdornment={
                isCurrencySymbolAfterAmount(locale)
                  ? convertCurrencyToCurrencySymbol(getCurrencyFromCountry(ticketing.organization.country))
                  : undefined
              }
              placeholder={getZeroAmountPlaceholder(locale)}
              data-test="extra-donation-amount-input"
              disableInjectingGlobalStyles={isPreview}
              {...manualExtraDonationField}
              onBlur={() => {
                onManualExtraDonationFieldBlur();
                logExtraDonationChangedEvent();
              }}
              onChange={handleOnManualExtraDonationChange}
              error={Boolean(errorMessage)}
              helperText={errorMessage}
              disabled={isDisabled}
            />
          </Box>
          {isExtraDonationReceiptEligible && (
            <FormTypography variant="body2" vibe="brand" sx={{ fontStyle: "italic" }}>
              {t("ticketing", "extraDonationTaxReceiptEligible")}
            </FormTypography>
          )}
        </Stack>
        {donorCanOptInGiftAid && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <GiftAidBlock
              amount={extraDonation}
              form={form as UseFormReturn<TicketingPaymentInput | DonationPaymentInput>}
              organizationName={ticketing.organization?.name || ""}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
};
